// Header Templates for Excel Editor
// Allows admins to import predefined headers or create custom ones

export interface HeaderTemplate {
  id: string;
  name: string;
  description: string;
  headers: string[];
  category: 'project' | 'task' | 'general' | 'custom';
}

// Predefined Header Templates
export const HEADER_TEMPLATES: HeaderTemplate[] = [
  {
    id: 'basic_task',
    name: 'Basic Task Management',
    description: 'Essential fields for task tracking',
    category: 'task',
    headers: ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date', 'Description']
  },
  {
    id: 'advanced_task',
    name: 'Advanced Task Management',
    description: 'Comprehensive task management with all fields',
    category: 'task',
    headers: ['Particulars', 'Concerned Person', 'Title', 'Status', 'Priority', 'Due Date', 'Start Date', 'Category', 'Created By', 'Description', 'Progress', 'Notes']
  },
  {
    id: 'project_management',
    name: 'Project Management',
    description: 'Complete project tracking system',
    category: 'project',
    headers: ['Project Name', 'Project Manager', 'Status', 'Priority', 'Start Date', 'End Date', 'Budget', 'Progress', 'Team Members', 'Description', 'Milestones', 'Risks']
  },
  {
    id: 'customer_service',
    name: 'Customer Service',
    description: 'Customer support and service tracking',
    category: 'custom',
    headers: ['Ticket ID', 'Customer Name', 'Issue Type', 'Status', 'Priority', 'Assigned To', 'Created Date', 'Due Date', 'Resolution', 'Customer Rating', 'Follow Up Date']
  },
  {
    id: 'inventory',
    name: 'Inventory Management',
    description: 'Product and inventory tracking',
    category: 'custom',
    headers: ['Product ID', 'Product Name', 'Category', 'Quantity', 'Unit Price', 'Supplier', 'Reorder Level', 'Last Updated', 'Location', 'Status', 'Notes']
  },
  {
    id: 'hr_management',
    name: 'HR Management',
    description: 'Human resources and employee tracking',
    category: 'custom',
    headers: ['Employee ID', 'Employee Name', 'Department', 'Position', 'Manager', 'Hire Date', 'Status', 'Performance Rating', 'Salary', 'Benefits', 'Notes']
  },
  {
    id: 'financial',
    name: 'Financial Tracking',
    description: 'Financial and budget management',
    category: 'custom',
    headers: ['Transaction ID', 'Description', 'Category', 'Amount', 'Date', 'Account', 'Status', 'Approved By', 'Payment Method', 'Reference', 'Notes']
  },
  {
    id: 'event_planning',
    name: 'Event Planning',
    description: 'Event and meeting management',
    category: 'custom',
    headers: ['Event Name', 'Event Type', 'Date', 'Time', 'Location', 'Organizer', 'Attendees', 'Status', 'Budget', 'Vendor', 'Notes']
  }
];

// Default headers that are always shown (core functionality)
// Attachment column is optional - admins can upload documents that users can view/download
export const CORE_HEADERS = ['Particulars', 'Concerned Person', 'Status', 'Priority', 'Due Date', 'Attachment'];

// Header categories for organization
export const HEADER_CATEGORIES = {
  task: 'Task Management',
  project: 'Project Management', 
  general: 'General Purpose',
  custom: 'Custom Templates'
};

// Function to get headers by category
export const getHeadersByCategory = (category: string): HeaderTemplate[] => {
  return HEADER_TEMPLATES.filter(template => template.category === category);
};

// Function to get template by ID
export const getTemplateById = (id: string): HeaderTemplate | undefined => {
  return HEADER_TEMPLATES.find(template => template.id === id);
};

// Function to validate header names
export const validateHeaderNames = (headers: string[]): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (headers.length === 0) {
    errors.push('At least one header is required');
  }
  
  if (headers.length > 20) {
    errors.push('Maximum 20 headers allowed');
  }
  
  // Check for duplicates
  const uniqueHeaders = new Set(headers.map(h => h.toLowerCase().trim()));
  if (uniqueHeaders.size !== headers.length) {
    errors.push('Duplicate header names are not allowed');
  }
  
  // Check for empty headers
  const emptyHeaders = headers.filter(h => !h.trim());
  if (emptyHeaders.length > 0) {
    errors.push('Empty header names are not allowed');
  }
  
  // Check for special characters
  const invalidHeaders = headers.filter(h => /[<>:"/\\|?*]/.test(h));
  if (invalidHeaders.length > 0) {
    errors.push('Header names cannot contain special characters: < > : " / \\ | ? *');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Function to suggest header names based on input
export const suggestHeaderNames = (input: string): string[] => {
  const suggestions: string[] = [];
  const lowerInput = input.toLowerCase().trim();
  
  // Get all unique headers from templates
  const allHeaders = [...new Set(HEADER_TEMPLATES.flatMap(t => t.headers))];
  
  // Find matching headers
  allHeaders.forEach(header => {
    if (header.toLowerCase().includes(lowerInput) || lowerInput.includes(header.toLowerCase())) {
      suggestions.push(header);
    }
  });
  
  // Add common suggestions
  const commonSuggestions = [
    'Particulars', 'Title', 'Name', 'Description', 'Status', 'Priority', 'Due Date',
    'Assigned To', 'Created By', 'Updated By', 'Category', 'Type', 'Amount',
    'Date', 'Time', 'Location', 'Contact', 'Email', 'Phone', 'Notes'
  ];
  
  commonSuggestions.forEach(suggestion => {
    if (suggestion.toLowerCase().includes(lowerInput) && !suggestions.includes(suggestion)) {
      suggestions.push(suggestion);
    }
  });
  
  return suggestions.slice(0, 10); // Return top 10 suggestions
};












